/**
 * Budget API Endpoints
 * Endpoints for transactions, budget limits, and budget summary
 */

import apiClient from '../client';
import type {
  Transaction,
  BudgetLimit,
  BudgetSummary,
  CsvColumnMapping,
  CsvPreviewRow,
  CsvImportPreviewResponse,
  CsvConfirmImportResponse,
  PdfImportPreviewResponse,
  AccountDetectionResponse,
  CreateAccountParams,
  FinancialAccount,
} from '../types';

// Forecast types
export interface ForecastData {
  current_balance: number;
  expected_debits_remaining: number;
  expected_income_remaining: number;
  projected_end_of_month: number;
  daily_budget: number;
  days_remaining: number;
  days_elapsed: number;
  total_income_so_far: number;
  total_expenses_so_far: number;
  avg_daily_variable_spend: number;
  projected_savings: number;
  month: string;
  savings_goal?: number;
}

// Recurring transaction types
export interface RecurringTransaction {
  description: string;
  normalized_key: string;
  category: string;
  transaction_type: string;
  amount: number;
  frequency: string;
  usual_day: number;
  next_expected: string;
  confidence: number;
  history: Array<{ date: string; amount: number }>;
  last_seen: string;
  occurrence_count: number;
  price_change?: { previous_amount: number; change: number };
  is_new?: boolean;
}

export interface RecurringResponse {
  recurring: RecurringTransaction[];
  total_monthly: number;
  count: number;
}

interface TransactionsParams {
  /** ISO date (YYYY-MM-DD) — start of the date range */
  from_date?: string;
  /** ISO date (YYYY-MM-DD) — end of the date range */
  to_date?: string;
  /** YYYY-MM — kept for backward-compat with the old month selector */
  month?: string;
  category?: string;
  transaction_type?: 'income' | 'expense';
  financial_account_id?: string;
  search?: string;
  tag?: string;
  tags?: string[];
  page?: number;
  per_page?: number;
  /** When true, includes transactions excluded from budget */
  include_excluded?: boolean;
}

interface TransactionsResponse {
  transactions: Transaction[];
  total_count: number;
  page: number;
  per_page: number;
}

interface BudgetSummaryParams {
  from_date?: string;
  to_date?: string;
  month?: string;
}

interface BudgetSummaryResponse {
  summary: BudgetSummary;
}

interface CreateTransactionData {
  description: string;
  amount: number;
  category: string;
  transaction_type: 'income' | 'expense';
  transaction_date: string;
  notes?: string;
  excluded_from_budget?: boolean;
  tags?: string[];
}

interface CreateBudgetLimitData {
  /** Either category or tag — exactly one. */
  category?: string;
  tag?: string;
  monthly_limit: number;
  name?: string;
  icon?: string;
  color?: string;
}

interface UpdateBudgetLimitData {
  monthly_limit?: number;
  name?: string;
  icon?: string;
  color?: string;
}

export const budgetApi = {
  /**
   * GET /transactions
   * Get transactions with optional filters. Returns total count and current
   * page from the X-Total / X-Page / X-Per-Page headers.
   */
  getTransactions: async (params?: TransactionsParams): Promise<TransactionsResponse> => {
    const response = await apiClient.get('/transactions', { params });
    const transactions = Array.isArray(response.data)
      ? response.data
      : response.data?.transactions || [];
    const totalCount = parseInt(response.headers?.['x-total'] || '0', 10);
    const page = parseInt(response.headers?.['x-page'] || '1', 10);
    const perPage = parseInt(response.headers?.['x-per-page'] || '50', 10);
    return { transactions, total_count: totalCount, page, per_page: perPage };
  },

  /**
   * POST /transactions
   * Create a new manual transaction.
   * Backend expects top-level params (no wrapper) and returns the bare entity.
   */
  createTransaction: async (data: CreateTransactionData): Promise<{ transaction: Transaction }> => {
    const response = await apiClient.post<Transaction>('/transactions', data);
    return { transaction: response.data };
  },

  /**
   * PUT /transactions/:id
   * Update an existing transaction.
   * Backend expects top-level params (no wrapper) and returns the bare entity.
   */
  updateTransaction: async (id: string, data: Partial<CreateTransactionData>): Promise<{ transaction: Transaction }> => {
    const response = await apiClient.put<Transaction>(`/transactions/${id}`, data);
    return { transaction: response.data };
  },

  /**
   * DELETE /transactions/:id
   * Cancel a transaction (soft delete)
   */
  deleteTransaction: async (id: string): Promise<void> => {
    await apiClient.delete(`/transactions/${id}`);
  },

  /**
   * GET /budget_limits
   * Get budget limits (category- or tag-based) with current month spending.
   */
  getBudgetLimits: async (month: string): Promise<BudgetLimit[]> => {
    const response = await apiClient.get<BudgetLimit[]>('/budget_limits', {
      params: { month },
    });
    return Array.isArray(response.data) ? response.data : (response.data as any)?.budget_limits || [];
  },

  /**
   * POST /budget_limits
   * Create a new budget limit (either category- or tag-based).
   */
  createBudgetLimit: async (data: CreateBudgetLimitData): Promise<BudgetLimit> => {
    const response = await apiClient.post<BudgetLimit>('/budget_limits', data);
    return response.data;
  },

  /**
   * PUT /budget_limits/:id
   * Update a budget limit
   */
  updateBudgetLimit: async (id: string, data: UpdateBudgetLimitData): Promise<BudgetLimit> => {
    const response = await apiClient.put<BudgetLimit>(`/budget_limits/${id}`, data);
    return response.data;
  },

  /**
   * DELETE /budget_limits/:id
   * Delete a budget limit
   */
  deleteBudgetLimit: async (id: string): Promise<void> => {
    await apiClient.delete(`/budget_limits/${id}`);
  },

  /**
   * GET /budget/summary
   * Get budget summary (defaults to current month if no params)
   */
  getBudgetSummary: async (params: BudgetSummaryParams): Promise<BudgetSummaryResponse> => {
    const response = await apiClient.get<BudgetSummaryResponse>('/budget/summary', {
      params,
    });
    return response.data;
  },

  /**
   * POST /transactions/import_csv
   * Parse CSV content and return preview for confirmation.
   * Column mapping is auto-detected by backend if omitted.
   */
  importCsv: async (
    csvContent: string,
    columnMapping?: CsvColumnMapping,
  ): Promise<CsvImportPreviewResponse> => {
    const response = await apiClient.post<CsvImportPreviewResponse>('/transactions/import_csv', {
      csv_content: csvContent,
      ...(columnMapping && { column_mapping: columnMapping }),
    });
    return response.data;
  },

  /**
   * POST /transactions/confirm_import
   * Persist previewed CSV transactions to database
   */
  confirmImport: async (
    transactions: CsvPreviewRow[],
    financialAccountId?: string,
    createAccount?: CreateAccountParams,
  ): Promise<CsvConfirmImportResponse> => {
    const response = await apiClient.post<CsvConfirmImportResponse>('/transactions/confirm_import', {
      transactions,
      ...(financialAccountId && { financial_account_id: financialAccountId }),
      ...(createAccount && { create_account: createAccount }),
    });
    return response.data;
  },

  /**
   * POST /transactions/import_pdf
   * Parse PDF content and return preview for confirmation.
   */
  importPdf: async (
    pdfContentBase64: string,
    financialAccountId?: string,
    bankName?: string,
  ): Promise<PdfImportPreviewResponse> => {
    const response = await apiClient.post<PdfImportPreviewResponse>('/transactions/import_pdf', {
      pdf_content: pdfContentBase64,
      ...(financialAccountId && { financial_account_id: financialAccountId }),
      ...(bankName && { bank_name: bankName }),
    });
    return response.data;
  },

  /**
   * POST /transactions/detect_account
   * Detect bank account info from CSV/PDF content for import wizard Step 2
   */
  detectAccount: async (
    csvContent: string,
    filename?: string,
    format?: 'csv' | 'pdf',
  ): Promise<AccountDetectionResponse> => {
    const response = await apiClient.post<AccountDetectionResponse>('/transactions/detect_account', {
      csv_content: csvContent,
      ...(filename && { filename }),
      ...(format && { format }),
    });
    return response.data;
  },

  /**
   * GET /financial_accounts
   * Get user's financial accounts
   */
  getFinancialAccounts: async (): Promise<FinancialAccount[]> => {
    const response = await apiClient.get<FinancialAccount[]>('/financial_accounts');
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * POST /financial_accounts
   * Create a new financial account
   */
  createFinancialAccount: async (data: {
    name: string;
    account_type: string;
    currency?: string;
    initial_balance?: number;
    institution_name?: string;
    include_in_budget?: boolean;
  }): Promise<FinancialAccount> => {
    const response = await apiClient.post<FinancialAccount>('/financial_accounts', data);
    return response.data;
  },

  /**
   * DELETE /financial_accounts/:id
   * Deactivate a financial account
   */
  deleteFinancialAccount: async (id: string): Promise<void> => {
    await apiClient.delete(`/financial_accounts/${id}`);
  },

  /**
   * PUT /financial_accounts/:id
   * Update a financial account
   */
  updateFinancialAccount: async (id: string, data: {
    name?: string;
    institution_name?: string;
    balance?: number;
    include_in_budget?: boolean;
  }): Promise<FinancialAccount> => {
    const response = await apiClient.put<FinancialAccount>(`/financial_accounts/${id}`, data);
    return response.data;
  },

  /**
   * GET /user_tags
   * Get user's tags for autocomplete
   */
  getUserTags: async (query?: string): Promise<Array<{ name: string; color: string | null; usage_count: number }>> => {
    const response = await apiClient.get('/user_tags', { params: query ? { query } : {} });
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * PATCH /transactions/:id/tags
   * Update tags on a transaction
   */
  updateTransactionTags: async (id: string, tags: string[]): Promise<Transaction> => {
    const response = await apiClient.patch(`/transactions/${id}/tags`, { tags });
    return response.data;
  },

  /**
   * GET /budget/forecast
   * Cashflow forecast for the current month (daily budget, projected end-of-month)
   */
  getForecast: async (): Promise<ForecastData> => {
    const response = await apiClient.get('/budget/forecast');
    return response.data;
  },

  /**
   * GET /budget/recurring
   * Detected recurring transactions (subscriptions, salary, etc.)
   */
  getRecurring: async (): Promise<RecurringResponse> => {
    const response = await apiClient.get('/budget/recurring');
    return response.data;
  },

  /**
   * GET /budget/expense_scan
   * Run the expense scanner over the user's transactions and return raw findings.
   */
  runExpenseScan: async (): Promise<{
    findings?: Array<{
      title?: string;
      description?: string;
      monthly_amount?: number;
      category?: string;
      severity?: string;
    }>;
    summary?: string;
    [k: string]: any;
  }> => {
    const response = await apiClient.get('/budget/expense_scan');
    return response.data;
  },

  /**
   * POST /budget/scanner/run_and_post
   * Runs the expense scanner and posts the formatted result directly into
   * the user's AI coach conversation. Returns the created message id so
   * the frontend can refresh the conversation to display it.
   */
  runScannerAndPost: async (): Promise<{
    success: boolean;
    message_id: string;
    findings_count: number;
  }> => {
    const response = await apiClient.post('/budget/scanner/run_and_post');
    return response.data;
  },
};
